import { useEffect, useState } from 'react';
import { getBoards, getRoster } from '../core/api';
import { SEASON_ID } from '../core/config';
import { useT } from '../core/lexicon';
import { useAsync } from '../core/useAsync';
import { Avatar } from '../core/sprite';
import { useApp } from '../App';
import { Screen, TopBar, CenterNote } from './Layout';
import type { BoardEntry, Boards, RosterMember } from '../core/types';

type TabKey = 'points' | 'guessing' | 'hiding';
const TABS: { key: TabKey; label: string; blurb: string }[] = [
  { key: 'points', label: 'board.main', blurb: 'board.main.blurb' },
  { key: 'guessing', label: 'board.guess', blurb: 'board.guess.blurb' },
  { key: 'hiding', label: 'board.evade', blurb: 'board.evade.blurb' },
];

/** All three boards, tabbed (spec §20). "Hiding" is high-is-good; the blurb says
 *  so, because it is the one board whose direction isn't obvious from its name. */
export function Standings() {
  const t = useT();
  const { user } = useApp();
  const boards = useAsync<Boards>(() => getBoards(SEASON_ID), []);
  const [tab, setTab] = useState<TabKey>('points');
  const [roster, setRoster] = useState<Map<number, RosterMember>>(new Map());
  useEffect(() => {
    getRoster()
      .then((list) => setRoster(new Map(list.map((m) => [m.user_id, m]))))
      .catch(() => {});
  }, []);

  const rows: BoardEntry[] = ((boards.data?.[tab] ?? []) as BoardEntry[])
    .slice()
    .sort((a, b) => b.total - a.total);

  return (
    <Screen>
      <TopBar title={t('nav.standings')} sub={t('season.label', { n: SEASON_ID }).toUpperCase()} right={`S${SEASON_ID}`} />

      <nav className="tabs">
        {TABS.map((tb) => (
          <button key={tb.key} className={tab === tb.key ? 'on' : ''} onClick={() => setTab(tb.key)}>
            {t(tb.label).toUpperCase()}
          </button>
        ))}
      </nav>

      <p className="blurb">{t(TABS.find((tb) => tb.key === tab)!.blurb)}</p>

      {boards.loading ? (
        <CenterNote>
          <div className="spin">LOADING…</div>
        </CenterNote>
      ) : boards.error ? (
        <CenterNote title={t('empty.no_results').toUpperCase()}>
          <p className="errline">{boards.error.message}</p>
        </CenterNote>
      ) : rows.length === 0 ? (
        <CenterNote title={t('empty.no_results').toUpperCase()} />
      ) : (
        <div className="list">
          {rows.map((r, i) => (
            <div key={r.user_id} className={`brow${r.user_id === user.user_id ? ' you' : ''}`}>
              <div className="pos">{String(i + 1).padStart(2, '0')}</div>
              <Avatar spriteData={roster.get(r.user_id)?.sprite_data ?? null} seed={roster.get(r.user_id)?.sprite_key || String(r.user_id)} size={36} />
              <div>
                <div className="nm">{r.display_name.toUpperCase()}</div>
                {tab === 'points' && r.from_predictions ? (
                  <div className="sub">
                    {r.from_tracks} + {r.from_predictions} {t('phase.predict.name').toLowerCase()}
                  </div>
                ) : null}
              </div>
              <div className="bval">{r.total}</div>
            </div>
          ))}
        </div>
      )}
    </Screen>
  );
}
